import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { useCreateDiary } from "../../../hooks/CALENDAR/useCreateDiary";
import { useUsers } from "../../../hooks/useGetUsers";
import { useAuth } from "../../../context/AuthContext";
import toast from "react-hot-toast";

const _pad = (n) => String(n).padStart(2, "0");
const _localDate = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
const _localTime = (d) => `${_pad(d.getHours())}:${_pad(d.getMinutes())}`;

const getNowParts = (baseDate) => {
  const now = new Date();
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  return {
    date: baseDate || _localDate(now),
    time: _localTime(now),
    dateEnd: baseDate || _localDate(end),
    timeEnd: _localTime(end),
  };
};

const NewAgendaEvent = ({ showNewAgendaEvent, setShowNewAgendaEvent, defaultDate }) => {
  const { user: authUser } = useAuth();
  const { mutate: createDiary, isPending } = useCreateDiary();
  const { data: usersData } = useUsers();

  const [label, setLabel] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [heureDebut, setHeureDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [heureFin, setHeureFin] = useState("");
  const [description, setDescription] = useState("");
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    if (!showNewAgendaEvent) return;
    const { date, time, dateEnd, timeEnd } = getNowParts(defaultDate);
    setDateDebut(date);
    setHeureDebut(time);
    setDateFin(dateEnd);
    setHeureFin(timeEnd);
    const me = authUser?.data;
    if (me) {
      setParticipants([{ value: me.id, label: `${me.surname} ${me.name}` }]);
    }
  }, [showNewAgendaEvent, defaultDate, authUser]);

  const userOptions =
    usersData?.data?.map((u) => ({
      value: u.id,
      label: `${u.surname} ${u.name}`,
    })) || [];

  const resetForm = () => {
    setLabel("");
    setDateDebut("");
    setHeureDebut("");
    setDateFin("");
    setHeureFin("");
    setDescription("");
    setParticipants([]);
  };

  const handleClose = () => {
    resetForm();
    setShowNewAgendaEvent(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const startDatetime = heureDebut
      ? `${dateDebut}T${heureDebut}:00`
      : `${dateDebut}T00:00:00`;

    const endDatetime =
      dateFin && heureFin
        ? `${dateFin}T${heureFin}:00`
        : dateFin
        ? `${dateFin}T00:00:00`
        : null;

    const payload = {
      label,
      start_date: startDatetime,
      ...(endDatetime && { end_date: endDatetime }),
      ...(description && { description }),
      participant_ids: participants.map((p) => p.value),
    };

    createDiary(payload, {
      onSuccess: () => {
        toast.success("Événement créé");
        handleClose();
      },
      onError: (err) => {
        toast.error(err.message || "Erreur lors de la création");
      },
    });
  };

  return (
    <Modal show={showNewAgendaEvent} onHide={handleClose} size="xs">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="modal-title text-client">Nouvel événement agenda</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col>
              <Form.Control
                type="text"
                placeholder="Titre *"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Date début *</Form.Label>
              <Form.Control
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                required
              />
            </Col>
            <Col>
              <Form.Label className="text-muted">Heure début</Form.Label>
              <Form.Control
                type="time"
                value={heureDebut}
                onChange={(e) => setHeureDebut(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Date fin</Form.Label>
              <Form.Control
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
              />
            </Col>
            <Col>
              <Form.Label className="text-muted">Heure fin</Form.Label>
              <Form.Control
                type="time"
                value={heureFin}
                onChange={(e) => setHeureFin(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Participant(s)</Form.Label>
              <Select
                isMulti
                options={userOptions}
                value={participants}
                onChange={setParticipants}
                styles={customSelectStyles}
                placeholder="Sélectionner..."
                noOptionsMessage={() => "Aucun utilisateur"}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={isPending}>
            {isPending ? "Création..." : "Créer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewAgendaEvent;
